import { useCallback, useMemo, useState } from "react";
import { z } from "zod";
import { ExpandedState } from "@tanstack/react-table";
import {
    ClientsWorkOverviewSortValue,
    ClientsWorkOverviewView,
} from "@common/types/views/clientsWorkOverviewView";
import { OrganizationCloseStage } from "@common/types/organizationCloseStage";
import { useCloseTemplates } from "@api/closeTemplate.api";
import { useOrganizationCloseStages } from "@api/organizationCloseStage.api";
import {
    ClientsWorkOverviewRow,
    ClientsWorkOverviewWorkRow,
    ClientsWorkOverviewGroupRow,
    ClientsWorkOverviewWorkspaceRow,
} from "./clientsWorkOverviewViewConfig";
import {
    getWorkDueDateGroups,
    getWorkTemplateGroups,
    WorkRowGroup,
} from "./workRowGroups";
import { getWorkStageGroups } from "./workStageGroups";
import { compareWorkRows } from "./workRowCompare";
import {
    getWorkPersonGroupRows,
    isWorkPersonRole,
    WORK_PERSON_ROLES,
} from "./workPersonGroups";

const BASE_GROUP_BYS = [
    "none",
    "stage",
    "client",
    "template",
    "due_date",
] as const;

export const GroupBySchema = z.enum([...BASE_GROUP_BYS, ...WORK_PERSON_ROLES]);
export type GroupBy = z.infer<typeof GroupBySchema>;

export const GROUP_BY_LABELS: Record<GroupBy, string> = {
    none: "No grouping",
    stage: "By stage",
    client: "By client",
    template: "By template",
    due_date: "By work end",
    preparer: "By preparer",
    reviewer: "By reviewer",
    manager: "By manager",
};

/** List's groupings, in the picker's order: the person groupings come last. */
export const GROUP_BY_OPTIONS: readonly GroupBy[] = [
    ...BASE_GROUP_BYS,
    ...WORK_PERSON_ROLES,
];

const NO_CLOSE_STAGES: OrganizationCloseStage[] = [];

interface UseGroupedTableDataParams {
    workspaceRows: ClientsWorkOverviewWorkspaceRow[] | null;
    view: ClientsWorkOverviewView;
    setView: (view: ClientsWorkOverviewView) => void;
    sort?: ClientsWorkOverviewSortValue;
    /** The groupings the page offers; a stored grouping it doesn't offer is none. */
    groupByOptions: readonly GroupBy[];
}

function sortWorkRows(
    rows: ClientsWorkOverviewWorkRow[],
    sort: ClientsWorkOverviewSortValue | undefined,
): ClientsWorkOverviewWorkRow[] {
    return [...rows].sort((a, b) => compareWorkRows(a, b, sort));
}

export function useGroupedTableData({
    workspaceRows,
    view,
    setView,
    sort,
    groupByOptions,
}: UseGroupedTableDataParams) {
    const storedGroupBy = GroupBySchema.catch("none").parse(
        view.options?.groupBy,
    );
    const groupBy = groupByOptions.includes(storedGroupBy)
        ? storedGroupBy
        : "none";
    const [expanded, setExpanded] = useState<ExpandedState>({});

    const handleGroupByChange = useCallback(
        (val: GroupBy | null) => {
            if (!val) {
                return;
            }
            setView({ ...view, options: { ...view.options, groupBy: val } });
            setExpanded({});
        },
        [setView, view],
    );

    const { data: closeTemplates = [], isLoading: isTemplatesLoading } =
        useCloseTemplates({
            enabled: groupBy === "template",
        });

    const {
        data: closeStages = NO_CLOSE_STAGES,
        isLoading: areCloseStagesLoading,
    } = useOrganizationCloseStages({
        enabled: groupBy === "stage",
    });

    const templateNameMap = useMemo(
        () => new Map(closeTemplates.map((t) => [t.id, t.name])),
        [closeTemplates],
    );

    const tableData = useMemo<ClientsWorkOverviewRow[] | null>(() => {
        if (workspaceRows === null) {
            return null;
        }
        if (groupBy === "client") {
            // Sort work rows within each workspace, keeping workspace
            // order as returned by the backend (already DB-sorted).
            return workspaceRows.map((wsRow) => ({
                ...wsRow,
                children: sortWorkRows(wsRow.children, sort),
            }));
        }
        const allWorkRows: ClientsWorkOverviewWorkRow[] = [];
        for (const wsRow of workspaceRows) {
            for (const workRow of wsRow.children) {
                allWorkRows.push(workRow);
            }
        }
        if (groupBy === "none") {
            return sortWorkRows(allWorkRows, sort);
        }
        if (isWorkPersonRole(groupBy)) {
            return getWorkPersonGroupRows({
                role: groupBy,
                rows: allWorkRows,
                sort,
            });
        }
        const toGroupRows = (groups: WorkRowGroup[]) =>
            groups
                .filter((group) => group.rows.length > 0)
                .map(
                    (group) =>
                        ({
                            id: group.id,
                            rowType: "group",
                            name: group.title,
                            children: sortWorkRows(group.rows, sort),
                        }) satisfies ClientsWorkOverviewGroupRow,
                );
        if (groupBy === "due_date") {
            return toGroupRows(getWorkDueDateGroups(allWorkRows));
        }
        if (groupBy === "stage") {
            if (areCloseStagesLoading) {
                return null;
            }
            return toGroupRows(
                getWorkStageGroups({ rows: allWorkRows, stages: closeStages }),
            );
        }
        // groupBy === "template": wait for template names before rendering groups
        if (isTemplatesLoading) {
            return null;
        }
        return toGroupRows(
            getWorkTemplateGroups({
                rows: allWorkRows,
                templateNames: templateNameMap,
            }),
        );
    }, [
        workspaceRows,
        groupBy,
        templateNameMap,
        isTemplatesLoading,
        closeStages,
        areCloseStagesLoading,
        sort,
    ]);

    return {
        groupBy,
        expanded,
        setExpanded,
        handleGroupByChange,
        tableData,
    };
}
